import React, { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { FormattedMessage, useIntl } from 'react-intl'
import { useAuth } from '../../contexts/AuthContext'
import { Button } from '../ui'
import ImpactComparisonBars from '../impact/ImpactComparisonBars'
import { buildCohortReport, COHORT_PARTICIPANTS } from '../../data/demo-cohort'
import { CONTACT } from '../../config/contact'

const HeroSection: React.FC = () => {
  const { user } = useAuth()
  const intl = useIntl()
  const report = useMemo(() => buildCohortReport(intl), [intl])

  return (
    <section className="py-10 sm:py-20 lg:py-28">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="lg:grid lg:grid-cols-2 lg:gap-16 items-center">
          {/* Left: the promise */}
          <div>
            <h1 className="text-4xl sm:text-5xl lg:text-[3.5rem] font-extrabold font-heading tracking-tight text-foreground leading-[1.08]">
              <FormattedMessage id="marketing.hero.titleLead" />{' '}
              <span className="text-primary"><FormattedMessage id="marketing.hero.titleHighlight" /></span>
            </h1>
            <p className="mt-6 text-lg text-foreground-muted max-w-xl">
              <FormattedMessage id="marketing.hero.subtitle" />
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              {user ? (
                <Link to="/dashboard" className="w-full sm:w-auto">
                  <Button variant="primary" size="lg" className="w-full sm:w-auto"><FormattedMessage id="marketing.hero.goToDashboard" /></Button>
                </Link>
              ) : (
                <>
                  <a href={CONTACT.bookingUrl} target="_blank" rel="noreferrer" className="w-full sm:w-auto">
                    <Button variant="primary" size="lg" className="w-full sm:w-auto"><FormattedMessage id="marketing.hero.talk" /></Button>
                  </a>
                  <Link to="/auth/signup" className="w-full sm:w-auto">
                    <Button variant="outline" size="lg" className="w-full sm:w-auto"><FormattedMessage id="marketing.hero.tryFree" /></Button>
                  </Link>
                </>
              )}
            </div>
            <p className="mt-6 text-sm text-foreground-muted max-w-lg">
              <FormattedMessage id="marketing.hero.madeIn" />
            </p>
          </div>

          {/* Right: the change itself, drawn by the component the app uses */}
          <div className="mt-10 lg:mt-0">
            <div className="bg-surface rounded-2xl shadow-lg border border-border-light p-4 sm:p-6">
              <div className="flex items-baseline justify-between gap-3 mb-1">
                <p className="text-xs text-foreground-muted uppercase tracking-wide font-semibold">
                  <FormattedMessage id="marketing.hero.visualTitle" />
                </p>
                <span className="text-lg font-bold text-success">
                  {report.overall_delta != null ? `+${report.overall_delta.toFixed(1)}` : ''}
                </span>
              </div>
              <p className="text-xs text-foreground-muted mb-4">
                <FormattedMessage id="marketing.hero.visualCaption" values={{ count: COHORT_PARTICIPANTS }} />
              </p>
              <ImpactComparisonBars report={report} bare />
              <p className="mt-3 text-[11px] leading-snug text-foreground-muted">
                <FormattedMessage id="marketing.cohort.fictional" />
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default HeroSection
